import React, { useEffect, useState } from 'react'
import { CardHeader, checkEncuesta, InfoCard, NameLabel, ThanksMessage, EncuestaLoading, getNextSurveyUrl, onFinishFailed } from '../utils'
import { Button, Card, Divider, Form, message, Radio, Slider, Typography } from 'antd'
import { cadss_catalog_1, cadss_catalog_2, responses } from './cads.catalog'
import { sendDataBody } from '../../resources'

const { Title, Paragraph, Text } = Typography;

const sectionCardStyle = { marginBottom: 24 }
const sectionHeaderStyle = { backgroundColor: '#f0f4ff' }

const markLeft  = { transform: 'translateX(0)', whiteSpace: 'normal', maxWidth: 110, fontSize: 12 }
const markRight = { transform: 'translateX(-100%)', whiteSpace: 'normal', maxWidth: 110, textAlign: 'right', fontSize: 12 }
const markMid   = { fontSize: 12 }

const sliderMarks = (left, right) => ({
    0:  { style: markLeft,  label: left },
    5:  { style: markMid,   label: '5' },
    10: { style: markRight, label: right },
})

const BooleanQuestion = ({ i }) => (
    <Form.Item
        label={<Text strong>{i + 1}. {cadss_catalog_1[i]}</Text>}
        name={i}
        rules={[{ required: true, message: 'Por favor responda la pregunta' }]}
    >
        <Radio.Group>
            <Radio value={true}>Sí</Radio>
            <Radio value={false}>No</Radio>
        </Radio.Group>
    </Form.Item>
)

const MultipleQuestion = ({ i }) => (
    <Form.Item
        label={<Text strong>{i + 1}. {cadss_catalog_2[i]}</Text>}
        name={i + 9}
        rules={[{ required: true, message: 'Por favor responda la pregunta' }]}
    >
        <Radio.Group>
            {responses[i].map((item, j) => (
                <Radio key={j} value={j} style={{ display: 'block', marginBottom: 6 }}>{item}</Radio>
            ))}
        </Radio.Group>
    </Form.Item>
)

export default function CadsEncuesta({ idpaciente, idmedico, token, visita, session, protocolo = "default", chain = '' }) {

    const [pacienteData, setPacienteData] = useState({})
    const [medicoData, setMedicoData] = useState({})
    const [encuestaNotExists, setEncuestaNotExists] = useState(false)
    const [checking, setChecking] = useState(true)

    useEffect(() => { init() }, [])

    function init() {
        checkEncuesta(idpaciente, visita, 'cadss', session, protocolo, setPacienteData, idmedico, setMedicoData, setEncuestaNotExists, setChecking)
    }

    const nextSurveyUrl = getNextSurveyUrl('cadss', chain, idmedico, idpaciente, token, visita, session, protocolo);

    const onFinish = (values) => {
        const body = {
            usuario: idpaciente,
            medico: idmedico,
            cadss: values,
            tipo: 'cadss',
            uuid: token,
            visita,
            sesion: session,
            protocolo
        }
        sendDataBody('encuestas/add', body)
            .then((rs) => message.success(rs.message))
            .then(() => init())
    };

    if (checking) return <EncuestaLoading />
    if (!encuestaNotExists) return <ThanksMessage nextUrl={nextSurveyUrl} scaleName="CADSS" />

    return (
        <div className='parentContainer'>
            <CardHeader title="Escala CADSS">
                <NameLabel pacienteData={pacienteData} />
                <NameLabel pacienteData={medicoData} label="Médico: " />
                <InfoCard label="Sesión" value={session} />
            </CardHeader>

            <div className='formContainer'>
                <Form
                    layout='vertical'
                    name="cads_enc"
                    initialValues={{ 15: 0, 16: 0, 17: 0 }}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                >
                    {/* Section 1 — Side effects checklist */}
                    <Card
                        title="Lista de verificación para efectos secundarios de esketamina"
                        style={sectionCardStyle}
                        styles={{ header: sectionHeaderStyle }}
                    >
                        {cadss_catalog_1.map((_, i) => <BooleanQuestion key={i} i={i} />)}
                    </Card>

                    {/* Section 2 — CADSS dissociative symptoms */}
                    <Card
                        title="Escala simplificada de síntomas disociativos (CADSS)"
                        style={sectionCardStyle}
                        styles={{ header: sectionHeaderStyle }}
                    >
                        <Paragraph type="secondary" style={{ marginBottom: 20 }}>
                            Escala de 6 elementos simplificada de síntomas disociativos administrada por el médico.
                        </Paragraph>
                        {cadss_catalog_2.map((_, i) => <MultipleQuestion key={i} i={i} />)}
                    </Card>

                    {/* Section 3 — Ketamine liking/craving */}
                    <Card
                        title="Cuestionario de gusto y apetencia a la Ketamina IV"
                        style={sectionCardStyle}
                        styles={{ header: sectionHeaderStyle }}
                    >
                        <Paragraph>
                            En este cuestionario, se le pedirá que proporcione respuestas de acuerdo con el grado de su experiencia,
                            seleccionando un valor en una escala del 0 al 10.
                        </Paragraph>

                        <Title level={5}>1. Gusto por la Ketamina IV</Title>
                        <Paragraph>
                            "Gusto" puede entenderse como el agrado en que uno busca los efectos psicoactivos positivos de una sustancia —
                            sentirse "elevado", separado de tu propio cuerpo, flotando, bienestar, emociones intensas o mayor sensibilidad sensorial.
                            Esto puede ser un efecto del tratamiento de Ketamina IV inmediatamente después de ser administrada.
                            "Gusto" se refiere a estos efectos, <Text strong>NO a ningún efecto antidepresivo o antisuicida.</Text>
                        </Paragraph>

                        <Form.Item
                            label="a) En general mi gusto por la ketamina IV es:"
                            name="15"
                            style={{ paddingBottom: 32 }}
                            rules={[{ required: true, message: 'Por favor responda la pregunta' }]}
                        >
                            <Slider dots min={0} max={10} marks={sliderMarks('0 - Me disgusta mucho', '10 - Me gusta mucho')} />
                        </Form.Item>

                        <Divider />

                        <Title level={5}>2. Apetencia por la Ketamina IV</Title>
                        <Paragraph>
                            "Apetencia" puede entenderse como el deseo de buscar los efectos psicoactivos positivos de una sustancia —
                            sentirse "elevado", separado de tu propio cuerpo, flotando, bienestar, emociones intensas o mayor sensibilidad sensorial.
                            "Apetencia" se refiere a estos efectos, <Text strong>NO a ningún efecto antidepresivo o antisuicida.</Text>
                        </Paragraph>

                        <Form.Item
                            label="a) En general, ¿cuánto anhela o apetece el tratamiento de la ketamina IV para otros efectos además de los antidepresivos?"
                            name="16"
                            style={{ paddingBottom: 32 }}
                            rules={[{ required: true, message: 'Por favor responda la pregunta' }]}
                        >
                            <Slider dots min={0} max={10} marks={sliderMarks('0 - Absolutamente no lo anhelo', '10 - Lo anhelo constantemente')} />
                        </Form.Item>

                        <Form.Item
                            label="b) ¿Cuánto anhela o apetece el tratamiento de la ketamina IV para otros efectos además de los antidepresivos?"
                            name="17"
                            style={{ paddingBottom: 32 }}
                            rules={[{ required: true, message: 'Por favor responda la pregunta' }]}
                        >
                            <Slider dots min={0} max={10} marks={sliderMarks('0 - Absolutamente no lo anhelo', '10 - Lo anhelo mucho')} />
                        </Form.Item>
                    </Card>

                    <Form.Item>
                        <Button type="primary" size="large" htmlType="submit">
                            Enviar respuestas
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </div>
    )
}
